import { cn } from "@jesus-central-church/shared";
import { cva, type VariantProps } from "class-variance-authority";
import { Image } from "../media/image";
import { generatePersonSchema } from "@jesus-central-church/schema-org";
import Script from "next/script";

const personCardVariants = cva("group overflow-hidden transition-all duration-300", {
  variants: {
    variant: {
      featured:
        "flex flex-col md:flex-row rounded-3xl bg-freedom dark:bg-boldness shadow-sm hover:shadow-xl",
      standard:
        "flex items-center gap-4 rounded-2xl bg-freedom dark:bg-boldness p-6 shadow-sm hover:shadow-md",
      compact:
        "flex items-center gap-4 rounded-2xl bg-freedom/50 dark:bg-boldness/50 p-6 border border-boldness/5 hover:bg-freedom dark:hover:bg-boldness",
    },
  },
  defaultVariants: {
    variant: "standard",
  },
});

const roleVariants = cva("uppercase tracking-wider font-bold", {
  variants: {
    variant: {
      featured: "text-xs px-4 py-1 rounded-full",
      standard: "text-xs text-strategy-gold",
      compact: "text-[10px] text-strategy-green tracking-widest",
    },
  },
  defaultVariants: {
    variant: "standard",
  },
});

export type PersonCardVariant = "featured" | "standard" | "compact";

export interface PersonCardProps extends VariantProps<typeof personCardVariants> {
  name: string;
  role: string;
  image?: {
    src: string;
    alt?: string;
  };
  quote?: string;
  roleColor?: "gold" | "green" | "red";
  className?: string;
  children?: React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

const getRoleColorClasses = (
  variant: PersonCardVariant | undefined,
  color?: "gold" | "green" | "red",
): string => {
  if (variant === "featured") {
    const colorMap = {
      gold: "bg-strategy-gold text-boldness",
      green: "bg-strategy-green text-freedom",
      red: "bg-strategy-red text-freedom",
    };
    return colorMap[color || "gold"];
  }
  if (variant === "compact") {
    return "text-strategy-green";
  }
  const colorMap = {
    gold: "text-strategy-gold",
    green: "text-strategy-green",
    red: "text-strategy-red",
  };
  return colorMap[color || "gold"];
};

export function PersonCard({
  name,
  role,
  image,
  quote,
  roleColor,
  variant = "standard",
  className,
  children,
  ...additionalProps
}: PersonCardProps) {
  const personSchema = generatePersonSchema({
    name,
    image: image?.src,
    role,
    description: quote,
  });

  if (variant === "featured") {
    return (
      <>
        <Script id="person-schema" type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }} />
        <article
          className={cn(personCardVariants({ variant }), className, "!min-h-[250px]")}
          {...additionalProps}
        >
          {/* Image */}
          <div className="relative h-80 w-full overflow-hidden md:h-auto md:w-2/5">
            {image?.src && (
              <Image
                src={image.src}
                alt={image.alt || name}
                fill
                className="object-cover transition-all duration-700 group-hover:scale-105"
                sizes="(max-width: 768px) 100vw, 40vw"
              />
            )}
            {/* Role badge */}
            <div className="absolute left-4 top-4">
              <span
                className={cn(
                  roleVariants({ variant }),
                  getRoleColorClasses(variant, roleColor),
                )}
              >
                {role}
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="flex flex-col justify-between p-8 md:w-3/5">
            <div>
              <h3 className="mb-2 font-heading text-4xl text-boldness dark:text-freedom">
                {name}
              </h3>
              {quote && (
                <p className="mb-6 text-sm italic text-boldness/70 dark:text-freedom/70">
                  "{quote}"
                </p>
              )}
            </div>
            {children && <div className="flex gap-4">{children}</div>}
          </div>
        </article>
      </>

    );
  }

  // Standard and Compact variants
  const imageSize = variant === "compact" ? "h-14 w-14" : "h-16 w-16";

  return (
    <article
      className={cn(personCardVariants({ variant }), className)}
      {...additionalProps}
    >
      {/* Avatar */}
      {image?.src && (
        <div className={cn("relative shrink-0 overflow-hidden rounded-full", imageSize)}>
          <Image
            src={image.src}
            alt={image.alt || name}
            fill
            className="object-cover"
            sizes="64px"
          />
        </div>
      )}

      {/* Content */}
      <div>
        <h4 className="text-lg font-bold leading-tight text-boldness dark:text-freedom">
          {name}
        </h4>
        <p
          className={cn(
            roleVariants({ variant }),
            getRoleColorClasses(variant as PersonCardVariant, roleColor),
          )}
        >
          {role}
        </p>
      </div>
    </article>
  );
}
